import * as geojson from 'geojson';
import * as I from 'immutable';

import {ApiFeatureData} from 'app/modules/Remote/Feature';
import {ALL_TIME_DATE_RANGE_LABEL, getSceneImageUrls} from 'app/stores/RasterCalculationStore';
import * as featureUtils from 'app/utils/featureUtils';
import * as mapUtils from 'app/utils/mapUtils';

export const MAX_FRAMES = 500;

// Scenes covering less of the property than this flash half-empty frames.
const PARTIAL_COVERAGE_RATIO = 0.95;

export interface TimelineFrame {
  cursor: string;
  date: Date;
  url: string;
  bounds: geojson.BBox;
  // Share of the property the scene covers; scenes without a recorded ratio count as full.
  coverage: number;
  isPartial: boolean;
}

export function getTimelineFrames(
  featureData: I.ImmutableOf<ApiFeatureData[]>,
  layerKey: string,
  includePartial: boolean
): TimelineFrame[] {
  return getSceneImageUrls(featureData, layerKey, {
    type: 'simple',
    label: ALL_TIME_DATE_RANGE_LABEL,
  })
    .filter(({url}) => mapUtils.isTilerUrl(url))
    .map(({date, url, datum}) => {
      const coverage: number = datum.getIn(['measurements', `${layerKey}_scene-coverage-ratio`], 1);
      return {
        cursor: datum.get('date'),
        date,
        url,
        bounds: featureUtils.getBboxFromBounds(
          I.fromJS(featureUtils.getFeatureBoundsforSource(datum, layerKey))
        ),
        coverage,
        isPartial:
          datum.getIn(['measurements', `${layerKey}_is-fully-ordered`]) === false ||
          coverage < PARTIAL_COVERAGE_RATIO,
      };
    })
    .filter((f) => includePartial || !f.isPartial);
}

export function getFrameUrl(url: string, maxSize: number): string {
  const parsedUrl = new URL(url);
  parsedUrl.pathname = parsedUrl.pathname.replace(/\/thumbnail\.png$/, '/thumbnail.webp');
  parsedUrl.searchParams.set('max_size', String(maxSize));
  return mapUtils.formatTilerThumbnailUrl(parsedUrl.toString());
}

// Sized in CSS pixels and capped at 1024: frames play as video, and larger ones
// slow both tiler renders and per-frame texture uploads on big properties.
export function getFrameMaxSize(map: mapboxgl.Map, bounds: geojson.BBox): number {
  const sw = map.project([bounds[0], bounds[1]]);
  const ne = map.project([bounds[2], bounds[3]]);
  const px = Math.max(Math.abs(ne.x - sw.x), Math.abs(ne.y - sw.y));
  return px <= 512 ? 512 : 1024;
}
